import { apiClient } from '@/services/api-client';
import type {
  CustomerPaymentMethod,
  CustomerPaymentMethodConfirmRequest,
  CustomerPaymentMethodSetupIntent,
} from '@/types/customer-payment-method';

type UnknownRecord = Record<string, unknown>;

const readRecord = (value: unknown): UnknownRecord =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as UnknownRecord) : {};

const readString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
};

const readNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const readBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') return true;
    if (normalized === 'false' || normalized === '0') return false;
  }
  return undefined;
};

const normalizePaymentMethod = (input: unknown): CustomerPaymentMethod => {
  const source = readRecord(input);
  const statusRaw = readString(source.status) ?? 'none';
  const status: CustomerPaymentMethod['status'] =
    statusRaw === 'none' || statusRaw === 'default' || statusRaw === 'error' ? statusRaw : 'none';

  const canUpdate = readBoolean(source.can_update ?? source.canUpdate);
  const canRemove = readBoolean(source.can_remove ?? source.canRemove);

  return {
    status,
    brand: readString(source.brand) ?? undefined,
    last4: readString(source.last4) ?? undefined,
    exp_month: readNumber(source.exp_month ?? source.expMonth) ?? undefined,
    exp_year: readNumber(source.exp_year ?? source.expYear) ?? undefined,
    funding: readString(source.funding) ?? undefined,
    message: readString(source.message) ?? undefined,
    can_update: canUpdate,
    can_remove: canRemove,
    blocked_reason_code: (readString(source.blocked_reason_code ?? source.blockedReasonCode) ?? null) as
      | string
      | null,
    blocked_reason: (readString(source.blocked_reason ?? source.blockedReason) ?? null) as string | null,
  };
};

const normalizeSetupIntent = (input: unknown): CustomerPaymentMethodSetupIntent => {
  const root = readRecord(input);
  const attributes = readRecord(root.attributes);
  const source = Object.keys(attributes).length ? attributes : root;

  const intentId =
    readString(source.intent_id ?? source.intentId) ?? readString(root.id) ?? '';
  const clientSecret =
    readString(source.client_secret ?? source.clientSecret) ?? '';
  const stripeIntentTypeRaw = readString(source.stripe_intent_type ?? source.stripeIntentType);
  const stripeIntentType =
    stripeIntentTypeRaw === 'setup' || stripeIntentTypeRaw === 'payment' ? stripeIntentTypeRaw : null;

  return { intentId, clientSecret, stripeIntentType };
};

class CustomerPaymentMethodService {
  async getPaymentMethod(): Promise<CustomerPaymentMethod> {
    const response = await apiClient.get<unknown>('/api/v1/customer/payment-method');
    return normalizePaymentMethod(response.data);
  }

  async createSetupIntent(): Promise<CustomerPaymentMethodSetupIntent> {
    const response = await apiClient.post<unknown>('/api/v1/customer/payment-method/setup-intent');
    return normalizeSetupIntent(response.data);
  }

  async confirmPaymentMethod(payload: CustomerPaymentMethodConfirmRequest): Promise<CustomerPaymentMethod> {
    const response = await apiClient.post<unknown, CustomerPaymentMethodConfirmRequest>(
      '/api/v1/customer/payment-method/confirm',
      payload,
    );
    return normalizePaymentMethod(response.data);
  }

  async removePaymentMethod(): Promise<CustomerPaymentMethod> {
    const response = await apiClient.delete<unknown>('/api/v1/customer/payment-method');
    return normalizePaymentMethod(response.data);
  }
}

export const customerPaymentMethodService = new CustomerPaymentMethodService();

